import { useEffect } from "react"

import bus from "@src/services/EventBus"
import { trackEvent } from "@src/utils/trackEvent"
import { useAnalyticsSuppressed } from "./useAnalyticsSuppressed"

const FIRST_ACTION_MAP = {
  swap_confirmed: "first_swap",
  deposit_success: "first_deposit",
  withdrawal_confirmed: "first_withdrawal",
} as const

type SourceEvent = keyof typeof FIRST_ACTION_MAP

const STORAGE_PREFIX = "defuse_"

function storageKey(event: string): string {
  return `${STORAGE_PREFIX}${event}`
}

function getTransactionIdPayload(payload: unknown): Record<string, unknown> {
  if (payload == null || typeof payload !== "object") return {}
  if (!("transaction_id" in payload)) return {}
  return { transaction_id: payload.transaction_id }
}

export function useFirstActionLogger() {
  const suppressed = useAnalyticsSuppressed()

  useEffect(() => {
    if (!bus || suppressed) return

    const entries = Object.entries(FIRST_ACTION_MAP) as Array<
      [SourceEvent, string]
    >

    const listeners: Array<() => void> = []

    for (const [source, firstEvent] of entries) {
      const listener = (payload: unknown) => {
        try {
          if (localStorage.getItem(storageKey(firstEvent))) return
          localStorage.setItem(storageKey(firstEvent), "1")
          trackEvent(firstEvent, getTransactionIdPayload(payload))
        } catch {
          // localStorage may be unavailable
        }
      }
      bus.on(source, listener)
      listeners.push(() => bus?.removeListener(source, listener))
    }

    return () => {
      for (const cleanup of listeners) {
        cleanup()
      }
    }
  }, [suppressed])
}
